'use client';
import { useInfiniteScroll } from 'ahooks';
import { useFormatter, useTranslations } from 'next-intl';
import { type ReactNode, type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { BaseLineDelete } from '@/app/components/icons/BaseLineDelete';
import { BaseLineMore } from '@/app/components/icons/BaseLineMore';
import { BaseLineShare } from '@/app/components/icons/BaseLineShare';
import { Loading } from '@/app/components/icons/Loading';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { LiveAvatarImage } from '@/app/components/shared/LiveAvatarImage';
import { SearchEmpty } from '@/app/components/shared/SearchEmpty';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { toast } from '@/app/components/ui/use-toast';
import { useGlobalModal, useScreenConfig } from '@/app/hooks';
import type { LiveV1Avatar, ServiceError } from '@/app/types';
import { reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { livesDeleteLivePlaybacks, livesListLivePlaybacks } from '../apis';
import type { LiveRecordTab } from '../constants';
import { DEFAULT_LIVE_RECORD_TAB, ITEMS_PER_ROW, PAGE_SIZE, PROCESSING_POLL_INTERVAL_MS } from '../constants';
import type { LiveRecordListData } from '../types';
import { getLiveRecordCardActions, isLiveRecordPending } from '../utils';
import { ShareLivePlaybackDialog } from './ShareLivePlaybackDialog';

const SCROLL_TARGET_ID = 'live-record-list-scroll';

// 回放接口的 avatar 为 LiveV1Avatar，类型上无 avatar_type 字段，与分享落地页一致用窄化断言读取，缺省按 pgc 处理。
const streamCharacterParams = (avatar?: LiveV1Avatar) => ({
  stream_character_name: avatar?.name?.trim() || '',
  stream_character_id: avatar?.id?.trim() || '',
  stream_character_type: (avatar as { avatar_type?: string } | undefined)?.avatar_type === 'user' ? 'ugc' : 'pgc',
});

export const LiveRecordList = ({
  embedded = false,
  scrollRef: externalScrollRef,
  title,
  stickyHeader,
}: {
  embedded?: boolean;
  scrollRef?: RefObject<HTMLDivElement | null>;
  title?: string;
  stickyHeader?: ReactNode;
}) => {
  const t = useTranslations();
  const format = useFormatter();
  const router = useRouter();
  const scrollRef = useRef<HTMLDivElement>(null);
  const listScrollRef = externalScrollRef ?? scrollRef;
  const [hasError, setHasError] = useState(false);
  const itemsPerRow = useScreenConfig<number>(ITEMS_PER_ROW);
  const { open: openConfirmDialog, close: closeConfirmDialog } = useGlobalModal('confirmDialog');

  const showFetchError = (error: ServiceError) => {
    toast({
      type: 'error',
      message: error?.message || t('popup.toast.fetchError'),
    });
  };

  /**
   * 拉取回放列表。分类交给服务端：tab 不是「全部」时带上 live_mode，
   * 前端不再对已分页的数据做二次过滤——那样每页各自过滤，切到子 tab 会只剩零星几条，
   * 用户以为「就这么多」，实际得反复下滚才凑得齐。
   *
   * 'all' 只是展示用的空筛选，不是一种归属，所以那一档不带 live_mode——
   * 传空串或 'all' 都会被服务端当成真实筛选值，一条都筛不出来。
   */
  const fetchList = (tab: LiveRecordTab, pageToken?: string): Promise<LiveRecordListData> =>
    livesListLivePlaybacks({
      query: {
        'pager.page_token': pageToken,
        'pager.pagesz': PAGE_SIZE,
        ...(tab === 'all' ? {} : { live_mode: tab }),
      },
    })
      .then(({ data, error }) => {
        if (error) {
          showFetchError(error as ServiceError);
          setHasError(!pageToken);
          return { list: [], nextPageToken: undefined };
        }
        setHasError(false);
        return {
          list: data?.items ?? [],
          nextPageToken: data?.next_page_token || undefined,
        };
      })
      .catch(() => {
        toast({ type: 'error', message: t('popup.toast.fetchError') });
        setHasError(!pageToken);
        return { list: [], nextPageToken: undefined };
      });

  const [activeTab, setActiveTab] = useState<LiveRecordTab>(DEFAULT_LIVE_RECORD_TAB);
  const {
    data,
    loading: firstLoading,
    loadMore,
    noMore,
    mutate,
    reload,
  } = useInfiniteScroll<LiveRecordListData>((d) => fetchList(activeTab, d?.nextPageToken), {
    target: listScrollRef,
    isNoMore: (d) => !d?.nextPageToken,
    // 切 tab 重新从第一页拉；否则会拿着上一个 tab 的 page_token 接着翻。
    reloadDeps: [activeTab],
  });

  const list = data?.list ?? [];
  const [shareTarget, setShareTarget] = useState<LiveRecordListData['list'][number] | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  /**
   * 切 tab：先把滚动条拉回顶部，再换 tab。
   *
   * 顺序不能反。useInfiniteScroll 每次请求成功后都会再判一次「是否触底」，触底就自动翻下一页。
   * 上一个 tab 如果已经滑了好几页，滚动条停在底部；此时切 tab，新分类第一页回来后那次判定
   * 仍然是「触底」，于是继续拉第 2、3、4 页……一次切换打出一串请求。
   * 归零之后新数据是从头看的，判定为未触底，只会保留必要的那一次请求。
   */
  const handleTabChange = (tab: LiveRecordTab) => {
    if (tab === activeTab) return;
    // 直接改 scrollTop 而不是 scrollTo()：后者在 jsdom 里不存在，抛错会把下面的
    // setActiveTab 一起带走，变成"点了 tab 完全没反应"。赋值属性哪里都能用。
    if (listScrollRef.current) {
      listScrollRef.current.scrollTop = 0;
    }
    setActiveTab(tab);
  };

  const tabOptions: Array<{ value: LiveRecordTab; label: string }> = [
    { value: 'all', label: t('liveRecords.tabs.all') },
    { value: 'editing', label: t('liveRecords.tabs.editing') },
    { value: 'avatar', label: t('liveRecords.tabs.avatar') },
  ];

  const handleDelete = async (playbackId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    openConfirmDialog({
      title: t('popup.dialog.delete.title'),
      description: t('popup.dialog.delete.content'),
      secondaryButtonText: t('common.dialog.cancel'),
      primaryButtonText: t('common.dialog.delete'),
      onPrimaryAction: async () => {
        setDeletingId(playbackId);

        try {
          const { error } = await livesDeleteLivePlaybacks({ body: { ids: [playbackId] } });

          if (error) {
            toast({
              message: t('liveRecords.delete.failed'),
              type: 'error',
            });
            return;
          }

          // Remove from local list
          mutate((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              list: prev.list.filter((it) => it.id !== playbackId),
            };
          });

          toast({
            message: t('liveRecords.delete.success'),
            type: 'success',
          });

          reportEvent('stream_element_click', {
            element_name: 'delete_watch_record_click',
          });
        } catch (_err) {
          toast({
            message: t('liveRecords.delete.failed'),
            type: 'error',
          });
        } finally {
          setDeletingId(null);
        }
      },
      onSecondaryAction: () => {
        closeConfirmDialog();
      },
    });
  };

  const processingIds = useMemo(() => list.filter(isLiveRecordPending).map((it) => it.id ?? ''), [list]);
  const processingKey = processingIds.join(',');

  useEffect(() => {
    if (!processingKey) return;
    const ids = processingKey.split(',').filter(Boolean);
    if (ids.length === 0) return;

    let cancelled = false;
    const timer = setInterval(async () => {
      const { data: reply, error } = await livesListLivePlaybacks({ query: { ids } });
      if (cancelled || error || !reply?.items?.length) return;
      const updates = new Map(reply.items.map((it) => [it.id, it]));
      mutate((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          list: prev.list.map((it) => (it.id && updates.has(it.id) ? { ...it, ...updates.get(it.id) } : it)),
        };
      });
    }, PROCESSING_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [processingKey, mutate]);

  const renderCard = (item: LiveRecordListData['list'][number]) => {
    const createdAtLabel = item.created_at
      ? format.dateTime(new Date(item.created_at), {
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        })
      : '';

    const isContentRejected = item.content_status === 'review_rejected' || item.content_status === 'review_failed';
    const isProcessing = !isContentRejected && isLiveRecordPending(item);
    const isGenerationFailed =
      item.status === 'failed' ||
      (item.status === 'completed' && item.content_status === 'allowed' && !item.watermarked_playback_url?.trim());
    const statusLabel = isContentRejected
      ? t('liveRecords.status.contentRejected')
      : isProcessing
        ? t('liveRecords.status.processing')
        : isGenerationFailed
          ? t('liveRecords.status.failed')
          : item.status === 'no_recording_too_short'
            ? t('liveRecords.status.tooShort')
            : null;

    const avatar = item.avatar;
    const avatarUrl = avatar?.avatar_url?.trim() || '';
    const trimmedId = item.id?.trim() ?? '';
    const { canDelete, canOpen, canShare } = getLiveRecordCardActions(item);

    return (
      <div
        key={item.id}
        role={canOpen ? 'button' : undefined}
        tabIndex={canOpen ? 0 : undefined}
        onClick={
          canOpen
            ? () => {
                reportEvent('stream_element_click', {
                  element_name: 'open_watch_record_click',
                  ...(item.live_mode === 'editing' ? {} : streamCharacterParams(item.avatar)),
                });
                router.push(`/vidu-stream/live-record/${trimmedId}`);
              }
            : undefined
        }
        onKeyDown={
          canOpen
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  reportEvent('stream_element_click', {
                    element_name: 'open_watch_record_click',
                    ...(item.live_mode === 'editing' ? {} : streamCharacterParams(item.avatar)),
                  });
                  router.push(`/vidu-stream/live-record/${trimmedId}`);
                }
              }
            : undefined
        }
        className={`group relative aspect-video w-full overflow-hidden rounded-8 bg-system-bg02 ${
          canOpen ? 'cursor-pointer' : ''
        }`}
      >
        {item.cover_url ? (
          <img src={item.cover_url} alt={item.id ?? 'live-record-cover'} className="h-full w-full object-contain" />
        ) : !statusLabel ? (
          <div className="flex h-full w-full items-center justify-center text-system-white32 text-xs">
            {t('liveRecords.card.noCover')}
          </div>
        ) : null}

        {statusLabel && (
          <div
            className={`absolute inset-0 flex items-center justify-center bg-black/50 px-4 text-center text-sm ${
              isProcessing ? 'text-white' : 'text-system-white64'
            }`}
          >
            {statusLabel}
          </div>
        )}

        {canDelete && (
          <div className="absolute top-2 right-2 z-10 flex gap-2">
            {canShare && (
              <button
                type="button"
                aria-label={t('liveRecords.share.title')}
                onClick={(e) => {
                  e.stopPropagation();
                  reportEvent('stream_element_click', {
                    element_name: 'asset_record_share_element_click',
                    ...(item.live_mode === 'editing' ? {} : streamCharacterParams(item.avatar)),
                  });
                  setShareTarget(item);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition hover:bg-black/70"
              >
                <BaseLineShare className="size-4" />
              </button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t('liveRecords.card.more')}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition hover:bg-black/70"
                >
                  <BaseLineMore className="size-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[120px]">
                <DropdownMenuItem
                  onClick={(e) => handleDelete(trimmedId, e)}
                  disabled={deletingId === trimmedId}
                  className="cursor-pointer text-red-500 focus:text-red-500"
                >
                  <BaseLineDelete className="mr-2 size-4" />
                  {deletingId === trimmedId ? t('liveRecords.delete.deleting') : t('liveRecords.delete.action')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/75 to-transparent px-2.5 py-2 text-white text-xs">
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            {item.live_mode !== 'editing' && (
              <LiveAvatarImage src={avatarUrl} name={avatar?.name ?? ''} className="h-6 w-6 text-xs" />
            )}
            {avatar?.name && <span className="truncate">{avatar.name}</span>}
          </div>
          {createdAtLabel && <span className="shrink-0 text-white/80">{createdAtLabel}</span>}
        </div>
      </div>
    );
  };

  return (
    <div className={embedded ? 'flex flex-col' : 'flex h-full flex-col'}>
      <div
        ref={embedded ? undefined : scrollRef}
        id={SCROLL_TARGET_ID}
        className={embedded ? undefined : 'min-h-0 flex-1 overflow-y-auto'}
      >
        <div
          className={`${
            stickyHeader ? 'top-0 z-30' : '-top-16 sm:-top-12 z-20'
          } sticky border-system-white08 border-b bg-black pb-1`}
        >
          {stickyHeader}
          {title ? <h2 className="pt-1 pb-2 font-medium text-xl">{title}</h2> : null}
          <div className="flex items-center gap-5 py-3">
            {tabOptions.map((option) => {
              const isActive = activeTab === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`relative whitespace-nowrap pb-1 text-sm transition-colors ${
                    isActive ? 'font-medium text-white' : 'text-system-white64 hover:text-system-white80'
                  }`}
                  onClick={() => handleTabChange(option.value)}
                >
                  {option.label}
                  {isActive && <span className="-bottom-1 absolute left-0 h-[2px] w-full rounded-full bg-white" />}
                </button>
              );
            })}
          </div>
        </div>
        {firstLoading ? (
          <div className="relative min-h-[calc(100dvh-220px)]">
            <GlobalLoader className="top-1/2" />
          </div>
        ) : list.length === 0 ? (
          <SearchEmpty
            emptyText={t(hasError ? 'popup.toast.fetchError' : 'liveRecords.empty')}
            actionText={hasError ? t('common.retry') : undefined}
            onAction={hasError ? reload : undefined}
          />
        ) : (
          <InfiniteScroll
            dataLength={list.length}
            next={loadMore}
            hasMore={!noMore}
            loader={
              <div className="flex justify-center py-4">
                <Loading />
              </div>
            }
            scrollableTarget={SCROLL_TARGET_ID}
            endMessage={<div className="py-4 text-center text-system-white32 text-xs">{t('liveRecords.noMore')}</div>}
          >
            <div className="grid gap-3 pb-3" style={{ gridTemplateColumns: `repeat(${itemsPerRow}, minmax(0, 1fr))` }}>
              {list.map(renderCard)}
            </div>
          </InfiniteScroll>
        )}
      </div>
      {shareTarget?.id && (
        <ShareLivePlaybackDialog
          open={shareTarget !== null}
          onOpenChange={(open) => !open && setShareTarget(null)}
          playbackId={shareTarget.id.trim()}
          liveId={shareTarget.live_id}
        />
      )}
    </div>
  );
};
